// ─── Custom SUI Hooks ────────────────────────────────────────────────────
// Wraps dapp-kit hooks with AGU-specific logic
import { useSuiClientQuery, useCurrentAccount } from '@mysten/dapp-kit';
import { AGU_TOKEN_TYPE, AGU_DECIMALS, OBJECTS, PACKAGE_ID } from './constants';

// Type helpers for SUI responses
type BalanceResult = { totalBalance: string };
type ObjectData = { data?: { content?: { dataType: string; fields?: Record<string, any> } } };
type CoinsResult = { data: Array<{ coinObjectId: string; balance: string }> };
type EventResult = { data: Array<Record<string, any>> };

// ─── AGU Balance ─────────────────────────────────────────────────────────
export function useAGUBalance() {
  const account = useCurrentAccount();

  const { data, isLoading, error, refetch } = useSuiClientQuery(
    'getBalance',
    { owner: account?.address ?? '', coinType: AGU_TOKEN_TYPE },
    { enabled: !!account?.address }
  );

  const bal = data as BalanceResult | undefined;
  const raw = BigInt(bal?.totalBalance ?? '0');
  const balance = Number(raw) / 10 ** AGU_DECIMALS;

  return {
    balance,
    rawBalance: raw,
    isLoading,
    error,
    refetch,
  };
}

// ─── Staking Pool Info ───────────────────────────────────────────────────
export function useStakingPool() {
  const { data, isLoading, error, refetch } = useSuiClientQuery(
    'getObject',
    {
      id: OBJECTS.stakingPool,
      options: { showContent: true },
    }
  );

  let poolInfo = {
    totalStaked: '0',
    rewardBalance: '0',
    stakeCount: 0,
    emissionRateBps: 0,
    minLockEpochs: 0,
  };

  const obj = data as ObjectData | undefined;
  if (obj?.data?.content?.dataType === 'moveObject') {
    const fields = obj.data.content.fields;
    if (fields) {
      poolInfo = {
        totalStaked: fields.total_staked || '0',
        rewardBalance: fields.reward_balance || '0',
        stakeCount: Number(fields.stake_count || 0),
        emissionRateBps: Number(fields.emission_rate_bps || 0),
        minLockEpochs: Number(fields.min_lock_epochs || 0),
      };
    }
  }

  return { poolInfo, isLoading, error, refetch };
}

// ─── Governance Config ───────────────────────────────────────────────────
export function useGovernanceConfig() {
  const { data, isLoading, error, refetch } = useSuiClientQuery(
    'getObject',
    {
      id: OBJECTS.governanceConfig,
      options: { showContent: true },
    }
  );

  let config = {
    proposalCount: 0,
    votingPeriodMs: 0,
    timelockMs: 0,
    quorumBps: 0,
  };

  if ((data as ObjectData)?.data?.content?.dataType === 'moveObject') {
    const fields = (data as ObjectData).data!.content!.fields;
    if (fields) {
      config = {
        proposalCount: Number(fields.proposal_count || 0),
        votingPeriodMs: Number(fields.voting_period_ms || 0),
        timelockMs: Number(fields.timelock_ms || 0),
        quorumBps: Number(fields.quorum_bps || 0),
      };
    }
  }

  return { config, isLoading, error, refetch };
}

// ─── AGU Treasury ────────────────────────────────────────────────────────
export function useAGUTreasury() {
  const { data, isLoading, error, refetch } = useSuiClientQuery(
    'getObject',
    {
      id: OBJECTS.aguTreasury,
      options: { showContent: true },
    }
  );

  let treasuryBalance = '0';

  if ((data as ObjectData)?.data?.content?.dataType === 'moveObject') {
    const fields = (data as ObjectData).data!.content!.fields;
    if (fields?.balance) {
      treasuryBalance = fields.balance;
    }
  }

  return {
    balance: Number(BigInt(treasuryBalance)) / 10 ** AGU_DECIMALS,
    rawBalance: BigInt(treasuryBalance),
    isLoading,
    error,
    refetch,
  };
}

// ─── User's Owned AGU Coins ──────────────────────────────────────────────
export function useAGUCoins() {
  const account = useCurrentAccount();

  const { data, isLoading, error, refetch } = useSuiClientQuery(
    'getCoins',
    { owner: account?.address ?? '', coinType: AGU_TOKEN_TYPE },
    { enabled: !!account?.address }
  );

  const result = data as CoinsResult | undefined;
  return {
    coins: result?.data ?? [],
    isLoading,
    error,
    refetch,
  };
}

// ─── Recent AGU token events ─────────────────────────────────────────────
export function useAGUEvents(eventType?: string) {
  const moduleEvent = eventType || `${PACKAGE_ID}::staking::StakeEvent`;

  const { data, isLoading, error, refetch } = useSuiClientQuery(
    'queryEvents',
    {
      query: { MoveEventType: moduleEvent },
      limit: 20,
      order: 'descending',
    }
  );

  const result = data as EventResult | undefined;
  return {
    events: result?.data ?? [],
    isLoading,
    error,
    refetch,
  };
}
